import React, { useEffect, useState } from "react";
import { useFirebaseAuth } from "../../../hooks/useFirebaseAuth";
import {
  Breadcrumbs,
  Button,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Paper,
  Table,
  TableContainer,
  Typography,
  IconButton,
  Tooltip,
  Link,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { Box } from "@mui/material";
import { Container } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { Loading } from "../../../components/Loading";
import { useNavigate } from "react-router-dom";
import AddPetrolPumpModal from "./AddPetrolPumpModal";
import axios from "axios";
import { useToast } from "../../../components/Toast";
import NavigationHeader from "../../../components/NavigationHeader";
import { tableCellStyles, tableHeaderStyles } from "../../../utils/styles";
import { formatCurrency } from "../../../utils/utils";

export interface PetrolPump {
  id?: string;
  petrolPumpName: string;
  primaryContactName: string;
  primaryContactNumber: string;
  bulkDiscountPerLitre: number;
  providerName: string;
  otherProviderName?: string;
  location: string;
  companyId?: string;
}

const PetrolPumps = () => {
  const [petrolPumps, setPetrolPumps] = useState<PetrolPump[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPetrolPump, setSelectedPetrolPump] = useState<
    PetrolPump | undefined
  >();
  const { user, loading: authLoading } = useFirebaseAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();

  useEffect(() => {
    if (user && !authLoading) {
      fetchPetrolPumpData();
    }
  }, [user, authLoading]);

  const fetchPetrolPumpData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}master-data/petrol-pumps`,
        {
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      if (response?.data?.isSuccess) {
        setPetrolPumps(response?.data?.data);
      }
    } catch (error) {
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPetrolPump = () => {
    setSelectedPetrolPump(undefined);
    setIsModalOpen(true);
  };

  const handleEditPetrolPump = (petrolPump: PetrolPump) => {
    setSelectedPetrolPump(petrolPump);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedPetrolPump(undefined);
  };

  const handleSubmit = async (petrolPumpData: any) => {
    setIsSubmitting(true);
    if (selectedPetrolPump) {
      // Update existing mechanic
      try {
        const response = await axios.put(
          `${process.env.REACT_APP_API_HOST}master-data/petrol-pumps/${petrolPumpData?.id}`,
          petrolPumpData,
          {
            headers: {
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
        if (response?.data?.isSuccess) {
          showToast("Petrol Pump updated succesfully", "success");
          fetchPetrolPumpData();
        } else {
          showToast(`Error updating petrol pump`, "error");
        }
      } catch (error) {
        console.error("Error updating petrol pump", error);
        showToast(`Error supdating petrol pump`, "error");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Add new mechanic
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_HOST}master-data/petrol-pumps`,
          petrolPumpData,
          {
            headers: {
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
        if (response?.data?.isSuccess) {
          showToast("Petrol Pump added succesfully", "success");
          fetchPetrolPumpData();
        } else {
          showToast(`Error submitting data`, "error");
        }
      } catch (error) {
        console.error("Error updating petrol pump", error);
        showToast(`Error updating petrol pump`, "error");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (isLoading || authLoading || isSubmitting) {
    return isSubmitting ? (
      <Loading message="Submitting..." fullScreen={false} />
    ) : (
      <Loading message="Loading petrol pumps..." fullScreen={false} />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Master Data", path: "/master-data" },
          { label: "Petrol Pumps" },
        ]}
        title="Petrol Pumps"
        button={{
          label: "Add Petrol Pump",
          onClick: handleAddPetrolPump,
          icon: <AddIcon />,
        }}
      />

      {petrolPumps.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
          No petrol pumps found
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="mechanics table">
            <TableHead>
              <TableRow>
                <TableCell {...tableHeaderStyles}>Name</TableCell>
                <TableCell {...tableHeaderStyles}>Contact Details</TableCell>
                <TableCell {...tableHeaderStyles}>Address</TableCell>
                <TableCell {...tableHeaderStyles}>
                  Bulk Discount Per Litre
                </TableCell>
                <TableCell {...tableHeaderStyles}>Provider Name</TableCell>
                <TableCell {...tableHeaderStyles} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {petrolPumps.map((petrolPump) => (
                <TableRow
                  key={petrolPump.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <TableCell {...tableCellStyles}>
                    {petrolPump.petrolPumpName}
                  </TableCell>
                  <TableCell {...tableCellStyles}>
                    {petrolPump.primaryContactName}
                    <br />
                    {petrolPump.primaryContactNumber}
                  </TableCell>
                  <TableCell {...tableCellStyles}>
                    {petrolPump.location || "-"}
                  </TableCell>
                  <TableCell {...tableCellStyles}>
                    {`${formatCurrency(
                      petrolPump.bulkDiscountPerLitre
                    )}/litre` || "-"}
                  </TableCell>
                  <TableCell {...tableCellStyles}>
                    {petrolPump.providerName || "-"}
                  </TableCell>
                  <TableCell {...tableCellStyles} align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditPetrolPump(petrolPump)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddPetrolPumpModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        petrolPump={selectedPetrolPump}
        isEdit={!!selectedPetrolPump}
      />
    </Container>
  );
};

export default PetrolPumps;
